/**
 * EventTypes to register on controls.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // access the enum
 * DMS.Events.ControlEventTypes.Click
 *
 * // register on a control
 * @@ctr:1337@@.on(DMS.Events.ControlEventTypes.Click, function() { alert('clicked') });
 * ```
 */
export enum ControlEventTypes {
    /**
     * Occurs when the control is clicked.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.Events.ControlEventTypes.Click
     * ```
     */
    Click = 0,

    /**
     * Occurs when a Control wants to resolve a script (replacements).
     * @hidden
     */
    Resolve = 1,

    /**
     * Occurs when a Control requests the DataItem (IDataRow) from the Grid.
     * @hidden
     */
    GetDataRow = 2,

    /**
     * Occurs when a Control value has changed.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.Events.ControlEventTypes.Change
     * ```
     */
    Change = 3,

    /**
     * Occurs before a command execution starts.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.Events.ControlEventTypes.BeforeCommand
     * ```
     */
    BeforeCommand = 4,

    /**
     * Occurs when a Control triggers the EditContext to check the required fields.
     * @hidden
     */
    CheckRequiredFields = 5,

    /**
     * Trigger BeforeExecute on Context.
     * @hidden
     */
    BeforeExecuteContext = 6,

    /**
     * Trigger BeforeExecute on Control.
     * @hidden
     */
    BeforeExecuteControl = 7,

    /**
     * Trigger AfterExecute on Context
     * @hidden
     */
    AfterExecuteContext = 8,

    /**
     * Trigger AfterExecute on Control
     * @hidden
     */
    AfterExecuteControl = 9,

    /**
     * Occurs after command execution is finished.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.Events.ControlEventTypes.AfterCommand
     * ```
     */
    AfterCommand = 10,

    /**
     * Occurs after command execution was cancelled.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.Events.ControlEventTypes.AfterCommandCancel
     * ```
     */
    AfterCommandCancel = 11,

    /**
     * Occurs after the focus on control was lost.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.Events.ControlEventTypes.LostFocus
     * ```
     */
    LostFocus = 12,

    /**
     * Occurs when a radio button was clicked.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.Events.ControlEventTypes.RadioButtonChange
     * ```
     */
    RadioButtonChange = 13,

    /**
     * Occurs when the MultipageUpdate-Button requests the changed data rows from EasyEdit.
     * @hidden
     */
    GetChangedDataRows = 14,

    /**
     * Occurs when the control is loaded.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.Events.ControlEventTypes.ControlLoaded
     * ```
     */
    ControlLoaded = 15,

    /**
     * @hidden
     */
    ControlReloadRequested = 16,

    /**
     * @hidden
     */
    HideGridColumn = 17,

    /**
     * @hidden
     */
    ShowGridColumn = 18,

    /**
     * @hidden
     */
    IsGridColumnVisible = 19,

    /**
     * Occurs when the control data was finished loading.
     * @since 4.0.1
     * @example
     * ```JavaScript
     * DMS.Events.ControlEventTypes.DataBound
     * ```
     */
    DataBound = 20,

    /**
     * @hidden
     */
    GetCommandOption = 21,

    /**
     * Occurs when the ColumnSelectorCommand-Button requests the selected rows from EasyEdit.
     * @hidden
     */
    GetColumnSelectorSelectedRows = 22,

    /**
     * Occurs after command execution had an error.
     * @hidden
     */
    AfterCommandError = 23,
}
